/**
 * ValueSparkline — small SVG chart showing 4 key points over time:
 *   Start, Highest NAV, Lowest NAV, and Latest (current) value.
 *
 * Props:
 *   documents  — timeline document array, each with { event_date, position_after: { nav } }
 *   currency   — ISO-4217 string (e.g. "USD", "ILS")
 *
 * Renders nothing when fewer than 2 data points have a non-null nav value.
 * X-axis is proportional to event_date. Y-axis is proportional to nav.
 */
export default function ValueSparkline({ documents = [], currency = "USD" }) {
  // ── Build sorted points ────────────────────────────────────────────────────
  const points = (documents || [])
    .filter((d) => d.event_date && d.position_after && d.position_after.nav != null)
    .map((d) => ({ date: d.event_date, nav: Number(d.position_after.nav) }))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  if (points.length < 2) return null;

  // ── Derive 4 key point indices ─────────────────────────────────────────────
  const startIdx = 0;
  const latestIdx = points.length - 1;

  let highIdx = 0;
  let lowIdx = 0;
  for (let i = 1; i < points.length; i++) {
    if (points[i].nav > points[highIdx].nav) highIdx = i;
    if (points[i].nav < points[lowIdx].nav) lowIdx = i;
  }

  // Assign roles and dedupe by index
  const roleMap = new Map(); // index → [role labels]
  const addRole = (idx, role) => {
    if (!roleMap.has(idx)) roleMap.set(idx, []);
    roleMap.get(idx).push(role);
  };
  addRole(startIdx, "Start");
  addRole(highIdx, "High");
  addRole(lowIdx, "Low");
  addRole(latestIdx, "Now");

  // Collect deduped key points sorted by date (ascending by index)
  const keyPointIndices = Array.from(roleMap.keys()).sort((a, b) => a - b);
  const keyPoints = keyPointIndices.map((idx) => ({
    idx,
    date: points[idx].date,
    nav: points[idx].nav,
    roles: roleMap.get(idx), // e.g. ["Start", "Low"] or ["Now"]
  }));

  // ── Layout constants ───────────────────────────────────────────────────────
  const WIDTH = 600;
  const HEIGHT = 115;
  const PADDING = { top: 28, right: 56, bottom: 28, left: 56 };

  const innerW = WIDTH - PADDING.left - PADDING.right;
  const innerH = HEIGHT - PADDING.top - PADDING.bottom;

  // Date range: use epoch milliseconds for proportional x
  const startMs = new Date(points[startIdx].date).getTime();
  const latestMs = new Date(points[latestIdx].date).getTime();
  const dateRange = latestMs - startMs || 1;

  const navValues = points.map((p) => p.nav);
  const minNav = Math.min(...navValues);
  const maxNav = Math.max(...navValues);
  const navRange = maxNav - minNav || 1;

  const toX = (date) =>
    PADDING.left + ((new Date(date).getTime() - startMs) / dateRange) * innerW;
  const toY = (nav) =>
    PADDING.top + innerH - ((nav - minNav) / navRange) * innerH;

  // ── Format helpers ─────────────────────────────────────────────────────────
  const safeCurrency = /^[A-Z]{3}$/.test(String(currency || "").trim().toUpperCase())
    ? String(currency).trim().toUpperCase()
    : "USD";

  const fmtCompact = (v) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: safeCurrency,
      notation: "compact",
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(v);

  // ── Polyline through deduped key points ───────────────────────────────────
  const linePoints = keyPoints.map((kp) => `${toX(kp.date)},${toY(kp.nav)}`).join(" ");

  // Area polygon: close the path along the bottom
  const firstX = toX(keyPoints[0].date);
  const lastX = toX(keyPoints[keyPoints.length - 1].date);
  const bottomY = PADDING.top + innerH;
  const areaPoints = `${firstX},${bottomY} ${linePoints} ${lastX},${bottomY}`;

  // ── Label positioning logic ────────────────────────────────────────────────
  // Determine vertical offset per role set: High gets label above dot, Low below,
  // Start and Now use side anchoring. When a point has multiple roles we pick
  // the dominant one for vertical placement.
  function labelProps(kp) {
    const roles = kp.roles;
    const isHigh = roles.includes("High");
    const isLow = roles.includes("Low");
    const isStart = roles.includes("Start");
    const isNow = roles.includes("Now");

    const cx = toX(kp.date);
    const cy = toY(kp.nav);
    const label = roles.join(" · ");
    const value = fmtCompact(kp.nav);

    // Vertical placement priority: High → above, Low → below, else above
    let dyRole, dyValue;
    if (isLow && !isHigh) {
      // Below the dot
      dyRole = cy + 14;
      dyValue = cy + 24;
    } else {
      // Above the dot (default for High, Start, Now, mixed)
      dyRole = cy - 14;
      dyValue = cy - 4;
    }

    // Horizontal anchor: Start pinned left, Now pinned right, others centered
    let textAnchor = "middle";
    if (isStart && !isNow) textAnchor = "start";
    if (isNow && !isStart) textAnchor = "end";

    // Clamp cx for start/end so text doesn't overflow the viewBox
    let labelX = cx;
    if (textAnchor === "start") labelX = Math.max(cx, PADDING.left);
    if (textAnchor === "end") labelX = Math.min(cx, WIDTH - PADDING.right);

    return { cx, cy, labelX, dyRole, dyValue, label, value, textAnchor };
  }

  return (
    <div className="mt-3 mb-1 px-1 max-w-3xl" data-testid="value-sparkline">
      <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">
        Current Value over time
      </p>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="w-full h-auto"
        aria-label="Current Value over time sparkline"
      >
        <defs>
          <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C41E2A" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#C41E2A" stopOpacity="0.01" />
          </linearGradient>
        </defs>

        {/* Soft fill area under the key-point line */}
        <polygon points={areaPoints} fill="url(#sparkFill)" />

        {/* Connecting line through key points */}
        <polyline
          points={linePoints}
          fill="none"
          stroke="#C41E2A"
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* Key point dots and labels */}
        {keyPoints.map((kp) => {
          const { cx, cy, labelX, dyRole, dyValue, label, value, textAnchor } =
            labelProps(kp);
          return (
            <g key={kp.idx} data-testid={`sparkline-point-${kp.roles.join("-").toLowerCase().replace(/\s/g, "")}`}>
              <circle cx={cx} cy={cy} r="3" fill="#C41E2A" />
              <text
                x={labelX}
                y={dyRole}
                textAnchor={textAnchor}
                fontSize="9"
                fontFamily="sans-serif"
                fill="#9ca3af"
                data-testid={`sparkline-role-${kp.roles.join("-").toLowerCase().replace(/[\s·]/g, "")}`}
              >
                {label}
              </text>
              <text
                x={labelX}
                y={dyValue}
                textAnchor={textAnchor}
                fontSize="9"
                fontFamily="sans-serif"
                fill="#4b5563"
                fontWeight="600"
                data-testid={`sparkline-value-${kp.roles.join("-").toLowerCase().replace(/[\s·]/g, "")}`}
              >
                {value}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
